import { type CompletedDraggingParams } from '@air/analytics';
import { type Board, type Clip, type LibraryWithPermissions } from '@air/api/types';

import { type GalleryItemType } from '~/components/Gallery/types';

export type DropType = 'move' | 'rearrange' | 'move-to-library' | 'rejected';

export type DropData<T = unknown> = {
  type: DropType;
  location: DropLocation;
  data?: T;
};

export enum DragType {
  boardNavFavorites = 'boardNavFavorites', // board dragged from nav bar - favorites section
  boardNavWorkspace = 'boardNavWorkspace', // board dragged from nav bar - workspace boards section
  boardNavLibrary = 'boardNavLibrary', // board dragged from nav bar - library boards section
  boardCard = 'board', // board dragged from gallery and table view
  asset = 'asset',
  file = 'file',
  workspace = 'workspace',
  version = 'version', //versions reordered in asset modal tab
  library = 'library', // library dragged from sidebar libraries section
}

export const boardDragTypes = [
  DragType.boardNavWorkspace,
  DragType.boardNavFavorites,
  DragType.boardCard,
  DragType.boardNavLibrary,
];

export const isBoardDragType = (type: DragType) => boardDragTypes.includes(type);

export type DragItem = {
  type: DragType;
  index?: number;
};

export type BoardDragItem = {
  type: DragType.boardCard | DragType.boardNavFavorites | DragType.boardNavWorkspace | DragType.boardNavLibrary;
  board: Board;
} & DragItem;

export type AssetDragItem = {
  type: DragType.asset | DragType.file;
  asset: Clip;
  assetId?: Clip['assetId'];
} & DragItem;

export type RearrangableItemType = GetAssetDragItem | GetBoardDragItem;

export type DraggingItems<FirstItem extends Board | Clip> = {
  /** This is the number that shows next to the thumbnail */
  count: number;

  /** First dragged item (we show its thumbnail and title) */
  firstItem: FirstItem;

  /** type of passed item */
  type: DragType;

  /** true if count > 2 and user drags items of different types, e.g. assets and boards */
  mixedTypes: boolean;
};

type GetItem<Type extends Board | Clip> = {
  id: string;
  index?: number;
} & DraggingItems<Type>;

export type GetAssetDragItem = {
  type: DragType.asset | DragType.file;
  assetId?: Clip['assetId'];
} & Required<GetItem<Clip>>;

export type GetBoardDragItem = {
  type: DragType.boardCard | DragType.boardNavWorkspace | DragType.boardNavFavorites | DragType.boardNavLibrary;
} & Required<GetItem<Board>> &
  Pick<Board, 'parentId'>;

export type DragLibraryItem = {
  type: DragType.library;
  id: string;
  index: number;
  library: LibraryWithPermissions;
};

export type DragWorkspaceItem = {
  type: DragType.workspace;
  id: string;
  index: number;
  image: string;
};

export type DragVersionItem = {
  type: DragType.version;
} & Omit<Clip, 'type'>;

export type DropLocation = CompletedDraggingParams['dropLocation']['type'];

export type DragContainers = 'workspace' | 'navBoard' | 'versions' | 'other';

export const DragContainerIds: { [key in DragContainers]: string } = {
  workspace: 'workspaces-nav-container',
  navBoard: 'fat-nav-container',
  versions: 'asset-modal-versions-tab',
  other: 'scrollContainer',
};

export type RearrangeItemBasic = {
  id: string;
  index: number;
};

export type ItemToRearrange = {
  type: DragType;
} & RearrangeItemBasic;

export type GalleryItemToRearrange = {
  mixedTypes: boolean;
} & ItemToRearrange;

export type RearrangeDirection = 'before' | 'after';

export type RearrangableItem = {
  direction: RearrangeDirection;
  index: number;
  type?: GalleryItemType;
};

export default DragType;
